import { useState } from 'react';
import { SettingsModal } from './SettingsModal';
import { ProModal } from './ProModal';
import { Leaderboard } from './GameOverScreen';

export const CharacterSelect = ({
  characters, isPro, adUnlocked, watchAdToUnlock,
  onSelect, onUnlockPro, onRestorePurchase, onPlayWithFriends,
  initAudio, playCoinSound,
  sfxOn, setSfxOn, isMusicOn, toggleMusic, vibrationOn, setVibrationOn,
  introOn, setIntroOn, outroOn, setOutroOn,
}) => {
  const [step, setStep] = useState('pick');
  const [pending, setPending] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPro, setShowPro] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // Only Mimi is free — Juma, Soni, Sam all locked behind Pro/Ad
  const isCharLocked = (char) => {
    if (isPro) return false;
    if (char.id === 'juma') return !adUnlocked.juma;
    if (char.id === 'soni') return !adUnlocked.soni;
    if (char.id === 'sam') return !adUnlocked.sam;
    return false;
  };

  const handleCharClick = (char) => {
    if (isCharLocked(char)) return; // blocked — must watch ad first
    initAudio();
    playCoinSound();
    setPending(char);
    setStep('mode');
  };

  const handleAdUnlockChar = async (charId, e) => {
    e.stopPropagation();
    await watchAdToUnlock(charId);
  };

  return (
    <div
      className="relative w-full max-w-md h-[100dvh] sm:h-[700px] mx-auto overflow-hidden bg-gradient-to-b from-indigo-900 via-purple-900 to-slate-900 shadow-2xl sm:rounded-3xl sm:border-8 sm:border-white select-none"
      data-testid="character-select-screen"
    >
      {/* Settings Overlay */}
      {showSettings && (
        <SettingsModal
          sfxOn={sfxOn} setSfxOn={setSfxOn}
          isMusicOn={isMusicOn} toggleMusic={toggleMusic}
          vibrationOn={vibrationOn} setVibrationOn={setVibrationOn}
          isPro={isPro} introOn={introOn} setIntroOn={setIntroOn}
          outroOn={outroOn} setOutroOn={setOutroOn}
          adUnlocked={adUnlocked} watchAdToUnlock={watchAdToUnlock}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Pro Overlay */}
      {showPro && (
        <ProModal
          onUnlock={(plan) => { onUnlockPro(plan); setShowPro(false); }}
          onRestore={() => { onRestorePurchase(); setShowPro(false); }}
          onClose={() => setShowPro(false)}
        />
      )}

      {/* Leaderboard Overlay */}
      <Leaderboard isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="absolute rounded-full bg-white/10"
            style={{
              width: `${8 + Math.random() * 16}px`, height: `${8 + Math.random() * 16}px`,
              left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
              animation: `float-particle ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }} />
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center h-full px-4 py-6">
        {/* Top bar */}
        <div className="w-full flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSettings(true)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
              data-testid="settings-button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            </button>
            <button onClick={() => setShowLeaderboard(true)}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-lg transition-all"
              data-testid="leaderboard-button">
              🏆
            </button>
          </div>
          {!isPro && (
            <button onClick={() => setShowPro(true)}
              className="px-3 py-1 rounded-full text-xs font-bold text-yellow-900 transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', fontFamily: 'Fredoka, sans-serif' }}
              data-testid="pro-button">
              PRO
            </button>
          )}
        </div>

        {step === 'pick' ? (
          <>
            <h1 className="game-title text-4xl text-white mb-2 tracking-wide" data-testid="character-select-title">
              Choose Your Flyer!
            </h1>
            <p className="text-white/60 text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Pick your character to start flying
            </p>

            <div className="grid grid-cols-2 gap-3 w-full max-w-xs flex-1 content-center">
              {characters.map((char) => {
                const locked = isCharLocked(char);
                return (
                  <div key={char.id}
                    onClick={() => !locked && handleCharClick(char)}
                    className={`group relative flex flex-col items-center p-3 rounded-2xl border-3 transition-all duration-200 cursor-pointer ${locked ? 'opacity-75' : 'hover:scale-105 active:scale-95'}`}
                    style={{
                      background: `linear-gradient(135deg, ${char.color}22, ${char.color}11)`,
                      borderColor: locked ? 'rgba(255,255,255,0.15)' : `${char.color}66`,
                      boxShadow: locked ? 'none' : `0 4px 20px ${char.color}20`,
                    }}
                    data-testid={`character-select-${char.id}`}>

                    {/* PRO lock overlay */}
                    {locked && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl bg-black/50 backdrop-blur-[2px]">
                        <div className="text-2xl mb-1">🔒</div>
                        <span className="text-[10px] font-bold text-amber-400 px-2 py-0.5 rounded-full mb-2"
                          style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.2), rgba(245,158,11,0.2))', fontFamily: 'Fredoka, sans-serif' }}>
                          PRO
                        </span>
                        <button
                          onClick={(e) => handleAdUnlockChar(char.id, e)}
                          className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:scale-110 active:scale-95"
                          style={{ background: 'linear-gradient(135deg, #a855f7, #7c3aed)', fontFamily: 'Fredoka, sans-serif', boxShadow: '0 2px 8px rgba(168,85,247,0.4)' }}
                          data-testid={`watch-ad-${char.id}`}
                        >
                          📺 Watch Ad
                        </button>
                      </div>
                    )}

                    <div className="relative w-28 h-28 rounded-xl overflow-hidden mb-2 border-2 transition-all duration-200"
                      style={{ borderColor: `${char.color}88`, background: `radial-gradient(circle, ${char.color}15, transparent)`, filter: locked ? 'grayscale(0.5)' : 'none' }}>
                      <img src={char.png} alt={char.name} className="w-full h-full object-contain p-1 transition-transform duration-200 group-hover:scale-110" draggable={false} />
                    </div>
                    <span className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      {char.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <h1 className="game-title text-3xl text-white mb-2 tracking-wide" data-testid="mode-select-title">
              Game Mode
            </h1>
            {pending && (
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 rounded-xl overflow-hidden border-2" style={{ borderColor: pending.color }}>
                  <img src={pending.png} alt="" className="w-full h-full object-contain" />
                </div>
                <span className="text-white text-xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {pending.name}
                </span>
              </div>
            )}

            <div className="flex flex-col gap-4 w-full max-w-xs mt-4">
              <button
                onClick={() => { playCoinSound(); onSelect(pending, 'solo'); setStep('pick'); }}
                className="w-full py-5 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 text-white text-xl font-bold shadow-lg transition-all hover:scale-105 active:scale-95"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="mode-solo">
                Solo Play
                <p className="text-xs font-normal text-white/70 mt-1">Just you and the pipes</p>
              </button>

              <button
                onClick={() => { playCoinSound(); onSelect(pending, 'fakeOnline'); setStep('pick'); }}
                className="w-full py-5 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white text-xl font-bold shadow-lg transition-all hover:scale-105 active:scale-95"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="mode-fake-online">
                Play Online
                <p className="text-xs font-normal text-white/70 mt-1">Race against 3 other players</p>
              </button>

              <button
                onClick={() => { playCoinSound(); onPlayWithFriends(pending); setStep('pick'); }}
                className="w-full py-5 rounded-2xl text-white text-xl font-bold shadow-lg transition-all hover:scale-105 active:scale-95"
                style={{ fontFamily: 'Fredoka, sans-serif', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', boxShadow: '0 4px 20px rgba(139,92,246,0.3)' }}
                data-testid="mode-friends">
                Play with Friends
                <p className="text-xs font-normal text-white/70 mt-1">Create or join a room</p>
              </button>

              <button
                onClick={() => { setStep('pick'); setPending(null); }}
                className="text-white/50 text-sm hover:text-white/80 transition-colors mt-2"
                style={{ fontFamily: 'Nunito, sans-serif' }}
                data-testid="back-to-characters">
                &larr; Back to characters
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
